import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Zap, Users, Plus, Trash2, Loader2, MessageSquare, Shield, User, Clock, Building2, Save
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const { token, user, isAdmin } = useAuth();
  const [quickReplies, setQuickReplies] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [scheduledMessages, setScheduledMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewReply, setShowNewReply] = useState(false);
  
  // Business Profile state
  const [businessProfile, setBusinessProfile] = useState('');
  const [originalProfile, setOriginalProfile] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  
  const [replyForm, setReplyForm] = useState({
    title: '',
    content: '',
    category: ''
  });

  const fetchQuickReplies = async () => {
    try {
      const response = await fetch(`${API_URL}/api/quick-replies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setQuickReplies(data);
      }
    } catch (error) {
      console.error('Error fetching quick replies:', error);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/team`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTeamMembers(data);
      }
    } catch (error) {
      console.error('Error fetching team:', error);
    }
  };

  const fetchScheduledMessages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/scheduled-messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setScheduledMessages(data);
      }
    } catch (error) {
      console.error('Error fetching scheduled messages:', error);
    }
  };

  const fetchBusinessProfile = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/business-profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBusinessProfile(data.system_prompt || '');
        setOriginalProfile(data.system_prompt || '');
      }
    } catch (error) {
      console.error('Error fetching business profile:', error);
    }
  };

  const handleSaveBusinessProfile = async () => {
    setSavingProfile(true);
    try {
      const response = await fetch(`${API_URL}/api/settings/business-profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ system_prompt: businessProfile })
      });

      if (response.ok) {
        toast.success('Perfil del negocio guardado exitosamente');
        setOriginalProfile(businessProfile);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al guardar el perfil');
      }
    } catch (error) {
      console.error('Error saving business profile:', error);
      toast.error('Error al guardar el perfil del negocio');
    } finally {
      setSavingProfile(false);
    }
  };

  useEffect(() => {
    Promise.all([
      fetchQuickReplies(),
      fetchTeamMembers(),
      fetchScheduledMessages(),
      fetchBusinessProfile()
    ]).finally(() => setLoading(false));
  }, [token]);

  const handleCreateReply = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/quick-replies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(replyForm)
      });

      if (response.ok) {
        toast.success('Respuesta rápida creada');
        fetchQuickReplies();
        setShowNewReply(false);
        setReplyForm({ title: '', content: '', category: '' });
      }
    } catch (error) {
      console.error('Error creating reply:', error);
      toast.error('Error al crear respuesta');
    }
  };

  const handleDeleteReply = async (replyId) => {
    try {
      const response = await fetch(`${API_URL}/api/quick-replies/${replyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Respuesta eliminada');
        fetchQuickReplies();
      }
    } catch (error) {
      console.error('Error deleting reply:', error);
      toast.error('Error al eliminar respuesta');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      const response = await fetch(`${API_URL}/api/team/${userId}/role?role=${newRole}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Rol actualizado');
        fetchTeamMembers();
      }
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Error al actualizar rol');
    }
  };

  const handleCancelScheduled = async (scheduledId) => {
    try {
      const response = await fetch(`${API_URL}/api/scheduled-messages/${scheduledId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Mensaje programado cancelado');
        fetchScheduledMessages();
      }
    } catch (error) {
      console.error('Error canceling message:', error);
      toast.error('Error al cancelar mensaje');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 text-[#25D366] animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8" data-testid="settings-container">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold font-['Outfit'] text-slate-900 mb-2">Configuración</h1>
        <p className="text-slate-500 mb-8">Gestiona tus respuestas rápidas, equipo y mensajes programados</p>

        <Tabs defaultValue="quick-replies" className="space-y-6">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="quick-replies" data-testid="tab-quick-replies">
              <Zap className="w-4 h-4 mr-2" />
              Respuestas rápidas
            </TabsTrigger>
            <TabsTrigger value="team" data-testid="tab-team">
              <Users className="w-4 h-4 mr-2" />
              Equipo
            </TabsTrigger>
            <TabsTrigger value="scheduled" data-testid="tab-scheduled">
              <Clock className="w-4 h-4 mr-2" />
              Programados
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="business-profile" data-testid="tab-business-profile">
                <Building2 className="w-4 h-4 mr-2" />
                Perfil del negocio
              </TabsTrigger>
            )}
          </TabsList>

          {/* Quick Replies Tab */}
          <TabsContent value="quick-replies" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold font-['Outfit']">Respuestas rápidas</h2>
                <p className="text-sm text-slate-500">Plantillas de mensajes reutilizables</p>
              </div>
              <Dialog open={showNewReply} onOpenChange={setShowNewReply}>
                <DialogTrigger asChild>
                  <Button data-testid="new-quick-reply-btn" className="bg-[#25D366] hover:bg-[#1DA851]">
                    <Plus className="w-4 h-4 mr-2" />
                    Nueva respuesta
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nueva respuesta rápida</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateReply} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Título *</Label>
                      <Input
                        id="title"
                        value={replyForm.title}
                        onChange={(e) => setReplyForm({ ...replyForm, title: e.target.value })}
                        placeholder="Ej: Saludo inicial"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="category">Categoría</Label>
                      <Input
                        id="category"
                        value={replyForm.category}
                        onChange={(e) => setReplyForm({ ...replyForm, category: e.target.value })}
                        placeholder="Ej: Saludos, Ventas, etc."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="content">Contenido *</Label>
                      <Textarea
                        id="content"
                        value={replyForm.content}
                        onChange={(e) => setReplyForm({ ...replyForm, content: e.target.value })}
                        placeholder="Escribe el mensaje..."
                        rows={4}
                        required
                      />
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="outline" onClick={() => setShowNewReply(false)}>
                        Cancelar
                      </Button>
                      <Button type="submit" className="bg-[#25D366] hover:bg-[#1DA851]">
                        Crear
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {quickReplies.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="text-center py-12">
                  <Zap className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">No hay respuestas rápidas</p>
                  <p className="text-sm text-slate-400 mt-1">Crea tu primera plantilla de mensaje</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {quickReplies.map((reply) => (
                  <Card key={reply.quick_reply_id} className="border-slate-200">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-medium text-slate-900">{reply.title}</h3>
                            {reply.category && (
                              <Badge variant="outline" className="text-xs">{reply.category}</Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-600">{reply.content}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteReply(reply.quick_reply_id)}
                          data-testid={`delete-reply-${reply.quick_reply_id}`}
                          className="text-slate-400 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Team Tab */}
          <TabsContent value="team" className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold font-['Outfit']">Equipo</h2>
              <p className="text-sm text-slate-500">Gestiona los miembros de tu equipo</p>
            </div>

            {teamMembers.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="text-center py-12">
                  <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">No hay miembros del equipo</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {teamMembers.map((member) => (
                  <Card key={member.user_id} className="border-slate-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#25D366] rounded-full flex items-center justify-center">
                            <span className="text-white font-medium">
                              {member.name?.charAt(0) || 'U'}
                            </span>
                          </div>
                          <div>
                            <h3 className="font-medium text-slate-900">{member.name}</h3>
                            <p className="text-sm text-slate-500">{member.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {isAdmin && member.user_id !== user?.user_id ? (
                            <Select
                              value={member.role}
                              onValueChange={(v) => handleUpdateRole(member.user_id, v)}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="agent">Agente</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge 
                              className={member.role === 'admin' 
                                ? 'bg-purple-100 text-purple-800' 
                                : 'bg-blue-100 text-blue-800'
                              }
                            >
                              {member.role === 'admin' ? (
                                <><Shield className="w-3 h-3 mr-1" />Admin</>
                              ) : (
                                <><User className="w-3 h-3 mr-1" />Agente</>
                              )}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Scheduled Messages Tab */}
          <TabsContent value="scheduled" className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold font-['Outfit']">Mensajes programados</h2>
              <p className="text-sm text-slate-500">Mensajes pendientes de envío</p>
            </div>

            {scheduledMessages.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="text-center py-12">
                  <Clock className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">No hay mensajes programados</p>
                  <p className="text-sm text-slate-400 mt-1">Programa mensajes desde la bandeja de entrada</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {scheduledMessages.map((msg) => (
                  <Card key={msg.scheduled_id} className="border-slate-200">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm text-slate-600 mb-2">{msg.content}</p>
                          <div className="flex items-center gap-4 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {format(new Date(msg.scheduled_time), 'dd/MM/yyyy HH:mm')}
                            </span>
                            <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                              Pendiente
                            </Badge>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCancelScheduled(msg.scheduled_id)}
                          data-testid={`cancel-scheduled-${msg.scheduled_id}`}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          Cancelar
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Business Profile Tab (Admin Only) */}
          {isAdmin && (
            <TabsContent value="business-profile" className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold font-['Outfit']">Perfil del negocio</h2>
                <p className="text-sm text-slate-500">
                  Configura el prompt del sistema para Claude AI. Este texto define cómo el bot y las sugerencias de respuesta entienden tu negocio.
                </p>
              </div>

              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Building2 className="w-5 h-5 text-[#25D366]" />
                    Prompt del sistema
                  </CardTitle>
                  <CardDescription>
                    Este texto se usará como contexto para el bot automático y las sugerencias de respuesta de IA.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="system-prompt">Instrucciones para la IA</Label>
                    <Textarea
                      id="system-prompt"
                      data-testid="business-profile-textarea"
                      value={businessProfile}
                      onChange={(e) => setBusinessProfile(e.target.value)}
                      placeholder="Describe tu negocio, servicios, tono de comunicación y reglas importantes..."
                      rows={12}
                      className="font-mono text-sm resize-y min-h-[200px]"
                    />
                    <p className="text-xs text-slate-400">
                      Incluye: descripción del negocio, servicios ofrecidos, tono de comunicación, reglas de cotización, y cualquier instrucción específica.
                    </p>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="text-sm text-slate-500">
                      {businessProfile !== originalProfile && (
                        <span className="text-amber-600 flex items-center gap-1">
                          <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></span>
                          Cambios sin guardar
                        </span>
                      )}
                    </div>
                    <div className="flex gap-3">
                      {businessProfile !== originalProfile && (
                        <Button
                          variant="outline"
                          onClick={() => setBusinessProfile(originalProfile)}
                          data-testid="reset-profile-btn"
                        >
                          Descartar cambios
                        </Button>
                      )}
                      <Button
                        onClick={handleSaveBusinessProfile}
                        disabled={savingProfile || businessProfile === originalProfile}
                        data-testid="save-profile-btn"
                        className="bg-[#25D366] hover:bg-[#1DA851]"
                      >
                        {savingProfile ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Guardando...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Guardar cambios
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-200 bg-blue-50/50">
                <CardContent className="p-4">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="text-sm text-slate-600">
                      <p className="font-medium text-slate-700 mb-1">¿Cómo funciona?</p>
                      <ul className="space-y-1 list-disc list-inside text-slate-500">
                        <li>El bot automático usará este perfil para responder a clientes fuera de horario.</li>
                        <li>Las sugerencias de IA se basarán en este contexto para generar respuestas relevantes.</li>
                        <li>Incluye instrucciones claras sobre precios, servicios y cómo manejar diferentes consultas.</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default Settings;
